import { Button, Icon, Spinner, ViewerContext } from "dash-ui";
import CompleteSignUpPageLayout from "flow-components/CompleteSignUpPageLayout/CompleteSignUpPageLayout";
import { RoutesPath } from "flow-components/routes/routes";
import { useGetCheckoutSessionDetailsQuery } from "gql/generated.graphql";
import React, { useContext, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTheme } from "styled-components";

function Ping({}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className="relative flex size-2">
      <div className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75"></div>
      <div className="relative inline-flex size-2 animate-pulse rounded-full bg-green-500"></div>
    </div>
  );
}

function ProcessingSubscription() {
  return (
    <>
      <div className="flex flex-row items-center">
        <Ping />{" "}
        <p className="ml-4">Please wait while we setup your subscription</p>
      </div>
      <p className="text-xs mt-4">
        Is this taking too long?{" "}
        <a
          href="#"
          className="cursor-pointer"
          onClick={() => {
            window.location.reload();
          }}>
          Reload the page
        </a>{" "}
        or <a href="#">contact DASH Support</a>
      </p>
    </>
  );
}

function ContinueSignUp() {
  const theme = useTheme();
  const navigate = useNavigate();

  return (
    <>
      <div className="flex flex-row items-center">
        <Icon name="checkCircle" size={20} color={theme.colors.g20} />
        <p className="ml-2">You're all set!</p>
      </div>
      <Button
        mt={4}
        onClick={() => {
          navigate(RoutesPath.Properties);
        }}>
        Start browsing!
      </Button>
    </>
  );
}

function SessionDetails({ sessionDetails }) {
  const { customerName, totalAmount, status } = sessionDetails || {};
  const amount = (totalAmount ?? 0) / 100;

  return (
    <div className="flex flex-col items-center">
      <p className="text-3xl font-bold">
        Thank you for your purchase, {customerName}!
      </p>
      <p className="text-xl mt-4">
        Your payment of{" "}
        <span className="text-sky-600 font-bold">${amount.toFixed(2)}</span> was
        processed successfully.
      </p>
    </div>
  );
}

function StripeCheckoutResultPage() {
  const { subscription } = useContext(ViewerContext);
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id") || "";

  // fetch session details
  const { data, error, loading } = useGetCheckoutSessionDetailsQuery({
    skip: !sessionId,
    variables: {
      sessionId: sessionId,
    },
  });

  const sessionDetails = data?.getCheckoutSessionDetails;

  useEffect(() => {
    if (!sessionId) {
      navigate(RoutesPath.CompleteSignUpPickSubscription);
    }
  }, [sessionId, navigate]);

  if (error) {
    return (
      <CompleteSignUpPageLayout activeStep={3}>
        <div className="h-[calc(100vh-200px)] w-full flex flex-col items-center justify-center px-8 md:px-0">
          <p className="text-2xl">
            Oops, there was an error processing your subscription!
          </p>
          <div className="flex flex-row gap-4 mt-4 items-center">
            <a href="https://help.dashtodash.com">Contact support</a>
          </div>
        </div>
      </CompleteSignUpPageLayout>
    );
  }

  if (!loading && (!sessionDetails || sessionDetails?.status === "expired")) {
    return (
      <CompleteSignUpPageLayout activeStep={3}>
        <div className="h-[calc(100vh-200px)] w-full flex flex-col items-center justify-center px-8 md:px-0">
          <p className="text-2xl">Oops, this page has expired!</p>
          <Button
            mt={4}
            onClick={() => {
              navigate(RoutesPath.Dashboard);
            }}>
            Go to Dashboard
          </Button>
        </div>
      </CompleteSignUpPageLayout>
    );
  }

  return (
    <CompleteSignUpPageLayout activeStep={3}>
      <div className="h-[calc(100vh-200px)] w-full flex flex-col items-center justify-center px-8 md:px-0">
        {loading && <Spinner />}
        {!loading && (
          <>
            <SessionDetails sessionDetails={sessionDetails} />
            <div className="mt-8 flex flex-col justify-center items-center">
              {/* we need to wait until the subscription is stored 
                in the database by the webhook handler. */}
              {subscription ? <ContinueSignUp /> : <ProcessingSubscription />}
            </div>
          </>
        )}
      </div>
    </CompleteSignUpPageLayout>
  );
}

export default StripeCheckoutResultPage;
